import { createPermissionPayload, Role, RoleAssignment } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { logger } from '@adornis/base/logging.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { SmartTeamsAppUser } from '@nsc/smartteams-shared/db/SmartTeamsAppUser.js';
import { SmartTeamsAppUserGroup } from '@nsc/smartteams-shared/db/SmartTeamsAppUserGroup.js';
import { SmartTeamsCustomer } from '@nsc/smartteams-shared/db/SmartTeamsCustomer.js';
import { upsertSmartTeamsAppUser } from '@nsc/smartteams-shared/api/upsert-app-user.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { SmartTeamsPermission, SmartTeamsRoleNames } from '../features/permissions/roles-and-permissions.js';

/**
 * Read the customerIds currently linked to a given AdornisUser. Used by the customer
 * assignment editor to populate the list. Returns [] when there is no SmartTeamsAppUser
 * record yet (e.g. the user just received the Customer role and hasn't been linked yet).
 */
export const getUserCustomerAssignments = registerQuery({
  operationName: 'getUserCustomerAssignments',
  type: () => [String],
  params: [{ name: 'userId', type: () => String }],
  options: { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } },
  resolve: (userId: string) => async () => {
    const allowed = await accessController.can(createPermissionPayload(SmartTeamsPermission.UserManage));
    if (!allowed) throw new Error('No permission to read user customer assignments');

    const appUserCol = await getRawCollection<SmartTeamsAppUser>(SmartTeamsAppUser._collectionName);
    const appUser = await appUserCol.findOne({ userId }, { projection: { customerIds: 1 } });
    return appUser?.customerIds ?? [];
  },
});

/**
 * Set the SmartTeamsCustomer entities a given app user represents. Used by the customer
 * assignment editor in admin user management. Idempotent — re-saves the full customerIds
 * array. Upserts the SmartTeamsAppUser record (creating it with userGroup=Customer when
 * missing) so the FE app's login validateUser hook lets the user in.
 *
 * The shared `upsertSmartTeamsAppUser` helper enforces the userGroup-match guard, so this
 * throws if the user is already onboarded as a non-Customer.
 */
export const setUserCustomerAssignments = registerMutation({
  operationName: 'setUserCustomerAssignments',
  type: () => SmartTeamsAppUser,
  params: [
    { name: 'userId', type: () => String },
    { name: 'customerIds', type: () => [String] },
  ],
  resolve: (userId: string, customerIds: string[]) => async (_gql: BaseQLSelectionSet<SmartTeamsAppUser>) => {
    const allowed = await accessController.can(createPermissionPayload(SmartTeamsPermission.UserManage));
    if (!allowed) throw new Error('No permission to manage user customer assignments');

    const userCol = await getRawCollection<AdornisUser>(AdornisUser._collectionName);
    const user = await userCol.findOne({ _id: userId }, { projection: { _id: 1, email: 1, username: 1 } });
    if (!user) throw new Error('User not found');

    // Validate all customer IDs reference real records.
    const customerCol = await getRawCollection<SmartTeamsCustomer>(SmartTeamsCustomer._collectionName);
    const ids = [...new Set(customerIds)];
    if (ids.length > 0) {
      const found = await customerCol.find({ _id: { $in: ids } }, { projection: { _id: 1 } }).toArray();
      if (found.length !== ids.length) {
        const missing = ids.filter(id => !found.some(c => c._id === id));
        throw new Error(`Unknown customer IDs: ${missing.join(', ')}`);
      }
    }

    const appUser = await upsertSmartTeamsAppUser({
      userId,
      userGroup: SmartTeamsAppUserGroup.Customer,
      customerIds: ids,
      // Only meaningful on first create; the helper skips $setting these when the row
      // already exists and the caller doesn't intend to override them.
      username: user.username ?? user.email ?? userId,
      email: user.email,
    });

    // Ensure the Customer role is assigned. Done after the upsert succeeds so we don't
    // leave a dangling role assignment if the upsert throws on a userGroup mismatch.
    const roleCol = await getRawCollection<Role>(Role._collectionName);
    let customerRole = await roleCol.findOne({ roleName: SmartTeamsRoleNames.CUSTOMER });
    if (!customerRole) {
      customerRole = new Role({ roleName: SmartTeamsRoleNames.CUSTOMER, permissions: [] });
      await customerRole.create();
      logger.info('Created placeholder Customer role on demand');
    }
    const raCol = await getRawCollection<RoleAssignment>(RoleAssignment._collectionName);
    const ra = await raCol.findOne({ userId });
    if (!ra) {
      await new RoleAssignment({ userId, roleIds: [customerRole._id] }).create();
    } else if (!ra.roleIds?.includes(customerRole._id)) {
      await raCol.updateOne({ _id: ra._id }, { $addToSet: { roleIds: customerRole._id } });
    }

    return appUser;
  },
});
